import React, { useEffect, useRef, useState } from 'react';
import Menu from 'rc-menu';
import styled, { css } from 'styled-components';
import { SearchBar } from './subcomponents';
import { getItemsIntoComponentsTransformer } from './util/getItemsIntoComponentsTransformer';
import { DropdownItem } from './subcomponents/types';

const useUpwardsResolver = (): [boolean, React.RefObject<HTMLDivElement>] => {
  const ref = useRef<HTMLDivElement>(null);
  const [upwards, setUpwards] = useState(false);

  useEffect(() => {
    if (ref?.current?.parentNode) {
      const menuNode = ref.current.parentNode as HTMLElement;
      const { y, height } = menuNode.getBoundingClientRect?.() ?? {};

      setUpwards(y + height > window.innerHeight);
    }
  }, []);

  return [upwards, ref];
};

export type MultiDropdownProps = {
  searchBar?: boolean;
  items: DropdownItem[];
  onSearch?: F<string, void>;
  searchQuery?: string;
  style?: React.CSSProperties;
  alwaysDown?: boolean;
};

const transformMainMenuItemsIntoComponents =
  getItemsIntoComponentsTransformer('0');

const StyledMenu = styled(Menu)<{ $bottomTop: boolean }>`
  position: absolute;
  max-height: 50vh;
  overflow: scroll;

  ${(p) =>
    p.$bottomTop &&
    css`
      bottom: 100%;
      top: auto;
    `}
`;

export const MultiDropdown: React.FC<MultiDropdownProps> = ({
  searchBar = false,
  items,
  onSearch,
  searchQuery = '',
  style = {},
  alwaysDown = false,
}) => {
  const [upwards, multiDropdownRef] = useUpwardsResolver();

  // the modular class below is used to make the searchbar sticky. The global class is used to make the .rc-menu-item-group-title items stick below the search bar.

  return (
    // DOCS: https://www.npmjs.com/package/rc-menu
    // TODO: potentially Windows/Linux users will want triggerSubMenuAction={'click'} instead of 'hover'
    <StyledMenu
      style={style}
      $bottomTop={!alwaysDown && upwards}
      triggerSubMenuAction="hover"
      subMenuOpenDelay={0.3}
      subMenuCloseDelay={0.3}
    >
      <div ref={multiDropdownRef} />
      {searchBar && onSearch && (
        <SearchBar
          key="0.search-bar"
          value={searchQuery}
          onChange={onSearch}
          autoFocus
        />
      )}
      {transformMainMenuItemsIntoComponents(items)}
    </StyledMenu>
  );
};

MultiDropdown.displayName = 'MultiDropdown';

export default MultiDropdown;
